import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, AlertTriangle, Download } from 'lucide-react';
import type { CSVValidationResult } from '../../types/asset-import.types';
import { assetImportService } from '../../services/asset-import.service';

interface CSVValidationResultsProps {
  result: CSVValidationResult;
  onImport: (assetIds: string[]) => void;
  onCancel: () => void;
}

export default function CSVValidationResults({
  result,
  onImport,
  onCancel
}: CSVValidationResultsProps) {
  const [showAllRows, setShowAllRows] = useState(false);

  const handleExportErrors = async () => {
    try {
      const blob = await assetImportService.exportValidationErrors(result.errors);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'import-errors.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export errors:', error);
    }
  };

  const handleImport = () => {
    const assetIds = result.validAssets.map((asset) => asset.id);
    onImport(assetIds);
  };

  const displayAssets = showAllRows ? result.validAssets : result.previewAssets;

  return (
    <div className="space-y-6">
      {/* Summary */}
      {result.errorCount > 0 ? (
        <Alert variant="destructive" className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-900">
            Validation Complete - {result.errorCount} error{result.errorCount !== 1 ? 's' : ''} found
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-900">
            Validation Passed - All {result.totalRows} rows are valid and ready to import
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Valid Rows
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{result.validRows}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-destructive">{result.errorCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Rows
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{result.totalRows}</div>
          </CardContent>
        </Card>
      </div>

      {/* Errors Table */}
      {result.errorCount > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Errors to Resolve</CardTitle>
            <Button variant="outline" size="sm" onClick={handleExportErrors}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Row</TableHead>
                    <TableHead>Field</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.errors.map((error, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{error.row}</TableCell>
                      <TableCell>{error.field}</TableCell>
                      <TableCell className="text-destructive">{error.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Table */}
      {result.validRows > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Preview</CardTitle>
            <span className="text-sm text-muted-foreground">
              Showing {displayAssets.length} of {result.validRows}
            </span>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayAssets.map((asset, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{asset.assetCode}</TableCell>
                      <TableCell>{asset.description}</TableCell>
                      <TableCell>{asset.category}</TableCell>
                      <TableCell className="text-right">
                        £{asset.boughtValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>{asset.boughtDate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {!showAllRows && result.validRows > 5 && (
              <Button
                variant="link"
                className="mt-2"
                onClick={() => setShowAllRows(true)}
              >
                Show all {result.validRows} rows
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          disabled={result.validRows === 0}
        >
          Import {result.validRows} {result.validRows === 1 ? 'Asset' : 'Assets'}
        </Button>
      </div>
    </div>
  );
}
